import * as THREE from 'three';

const _vec = new THREE.Vector3();

/**
 * Cached canvas size. `getBoundingClientRect()` forces a layout read, and
 * worldToScreen runs several times per frame across the betting overlays
 * (sidebet buttons, cancel button, zone labels). The canvas is a fixed,
 * full-screen element, so its size only changes on a resize / viewport change —
 * we cache width/height and invalidate on those events instead of paying a
 * forced reflow on every call.
 */
let _rectCanvas: HTMLCanvasElement | null = null;
let _rectW = 0;
let _rectH = 0;
let _listenersAttached = false;

function ensureInvalidationListeners(): void {
  if (_listenersAttached || typeof window === 'undefined') return;
  const invalidate = () => { _rectCanvas = null; };
  window.addEventListener('resize', invalidate);
  // Mobile address-bar show/hide changes the visual viewport without a classic
  // 'resize' on some browsers.
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', invalidate);
  }
  _listenersAttached = true;
}

/**
 * Cached `{ width, height }` of the canvas bounding rect. Recomputed only when
 * invalidated (resize / viewport change) or when a different canvas is queried.
 * Lets per-frame callers avoid a forced layout on every call.
 */
export function getCanvasSize(canvas: HTMLCanvasElement): { width: number; height: number } {
  ensureInvalidationListeners();
  if (_rectCanvas !== canvas) {
    const r = canvas.getBoundingClientRect();
    _rectCanvas = canvas;
    _rectW = r.width;
    _rectH = r.height;
  }
  return { width: _rectW, height: _rectH };
}

/**
 * Project a 3D world position to CSS pixel coordinates relative to the canvas.
 * Returns null if the point is behind the camera.
 */
export function worldToScreen(
  worldPos: { x: number; y: number; z: number },
  camera: THREE.PerspectiveCamera,
  canvas: HTMLCanvasElement
): { x: number; y: number } | null {
  _vec.set(worldPos.x, worldPos.y, worldPos.z);
  _vec.project(camera);

  // Behind camera
  if (_vec.z > 1) return null;

  const { width, height } = getCanvasSize(canvas);
  return {
    x: (_vec.x * 0.5 + 0.5) * width,
    y: (-_vec.y * 0.5 + 0.5) * height,
  };
}
